import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Languages, Loader2, Plus, X, Star } from "lucide-react";

/**
 * Multi-language text input with a Translate button.
 *
 * value can be:
 *   - undefined / "" → empty
 *   - a plain string → treated as the base-language value
 *   - an object shape: { __base: "en", en: "…", ml: "…", es: "…" }
 *
 * onChange(nextValue) always emits the object shape once the user adds a
 * second language; if they only ever type in one language it stays as a
 * plain string for backward compatibility.
 *
 * Props:
 *   label            : short label above the field
 *   value            : string | i18n dict | null
 *   onChange         : (next) => void
 *   as               : "input" (default) | "textarea"
 *   rows             : rows for textarea
 *   placeholder      : optional placeholder
 *   testid           : test id for the primary field
 *   required         : passthrough for the primary base-language input
 *   inputCls         : optional extra tailwind classes
 */
export default function TranslatableInput({
  label,
  value,
  onChange,
  as = "input",
  rows = 3,
  placeholder,
  testid,
  required,
  inputCls,
}) {
  const [languages, setLanguages] = useState([]);
  const [open, setOpen] = useState(false);
  const [translating, setTranslating] = useState(null); // lang code currently being translated
  const [showAdd, setShowAdd] = useState(false);
  const [pickedLang, setPickedLang] = useState("");
  const popRef = useRef(null);

  useEffect(() => {
    http.get("/i18n/languages").then((r) => setLanguages(r.data?.languages || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!popRef.current) return;
      if (open && !popRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  // Normalise the value: work internally with { __base, [code]: text }
  const asDict = (v) => {
    if (!v) return { __base: "en", en: "" };
    if (typeof v === "string") return { __base: "en", en: v };
    const dict = { __base: v.__base || "en" };
    for (const [k, val] of Object.entries(v)) {
      if (k === "__base") continue;
      if (typeof val === "string") dict[k] = val;
    }
    if (!dict[dict.__base]) dict[dict.__base] = "";
    return dict;
  };
  const dict = asDict(value);
  const codes = Object.keys(dict).filter((k) => k !== "__base");
  const base = dict.__base;

  const emit = (nextDict) => {
    if (!nextDict) { onChange(""); return; }
    const filled = Object.entries(nextDict).filter(([k, v]) => k !== "__base" && (v ?? "").trim().length > 0);
    if (filled.length === 0) { onChange(""); return; }
    if (filled.length === 1 && filled[0][0] === (nextDict.__base || "en")) {
      // Single-language → emit plain string for back-compat with older callers
      onChange(filled[0][1]);
      return;
    }
    onChange({ __base: nextDict.__base || filled[0][0], ...Object.fromEntries(filled) });
  };

  const setLangText = (code, text) => {
    const next = { ...dict, [code]: text };
    emit(next);
  };
  const removeLang = (code) => {
    const next = { ...dict };
    delete next[code];
    if (next.__base === code) next.__base = Object.keys(next).find((k) => k !== "__base") || "en";
    emit(next);
  };
  const setBase = (code) => {
    emit({ ...dict, __base: code });
  };

  const translateInto = async (targetLang) => {
    const sourceText = (dict[base] || "").trim();
    if (!sourceText) { toast.error("Enter text first, then translate."); return; }
    setTranslating(targetLang);
    try {
      const { data } = await http.post("/i18n/translate", { text: sourceText, target_lang: targetLang, source_lang: base });
      setLangText(targetLang, data.translated || sourceText);
      toast.success(`Translated to ${languageName(targetLang)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(null);
    }
  };

  const addLang = () => {
    if (!pickedLang) return;
    if (dict[pickedLang] !== undefined) { toast.error("Already added"); return; }
    // Prepare an empty entry then translate on demand.
    setLangText(pickedLang, "");
    setShowAdd(false);
    setPickedLang("");
  };

  const languageName = (code) => (languages.find((l) => l.code === code) || {}).name || code.toUpperCase();

  const commonCls = `w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent ${inputCls || ""}`;
  const borderStyle = { borderColor: "var(--wofy-border)" };

  return (
    <div className="block" ref={popRef}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border hover:bg-black/5"
          style={borderStyle}
          data-testid={testid ? `${testid}-translate-btn` : undefined}
          title="Add other languages"
        >
          <Languages className="w-3 h-3" />
          {codes.length > 1 ? `${codes.length} langs` : "Translate"}
        </button>
      </div>

      {as === "textarea" ? (
        <textarea
          rows={rows}
          placeholder={placeholder}
          value={dict[base] || ""}
          onChange={(e) => setLangText(base, e.target.value)}
          className={commonCls}
          style={borderStyle}
          required={required}
          data-testid={testid}
        />
      ) : (
        <input
          placeholder={placeholder}
          value={dict[base] || ""}
          onChange={(e) => setLangText(base, e.target.value)}
          className={commonCls}
          style={borderStyle}
          required={required}
          data-testid={testid}
        />
      )}

      {/* Language chips row — visible whenever there's more than one lang, or the popover is open */}
      {(codes.length > 1 || open) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {codes.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setBase(code)}
              className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border ${base === code ? "bg-black text-white border-black" : "hover:bg-black/5"}`}
              style={base !== code ? borderStyle : undefined}
              title={`Edit ${languageName(code)}`}
              data-testid={testid ? `${testid}-lang-${code}` : undefined}
            >
              {base === code && <Star className="w-3 h-3 fill-current" />}
              {code.toUpperCase()}
              {code !== "en" && (
                <span onClick={(e) => { e.stopPropagation(); removeLang(code); }} className="ml-1 opacity-60 hover:opacity-100"><X className="w-3 h-3" /></span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Popover: pick a language to translate into or add empty */}
      {open && (
        <div className="mt-2 rounded-xl border p-3" style={borderStyle} data-testid={testid ? `${testid}-lang-popover` : undefined}>
          <div className="text-[11px] font-mono uppercase tracking-wider mb-2 opacity-70">Add / translate into another language</div>
          <div className="flex flex-wrap gap-1 mb-2">
            {languages.slice(0, 24).filter((l) => l.code !== base).map((l) => (
              <button
                key={l.code}
                type="button"
                disabled={translating === l.code}
                onClick={() => translateInto(l.code)}
                className={`text-[11px] px-2 py-1 rounded-full border hover:bg-black/5 ${dict[l.code] !== undefined ? "opacity-60" : ""}`}
                style={borderStyle}
                data-testid={testid ? `${testid}-translate-to-${l.code}` : undefined}
              >
                {translating === l.code ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                {l.name} ({l.code})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowAdd((v) => !v)} className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border" style={borderStyle}>
              <Plus className="w-3 h-3 inline" /> Add empty language
            </button>
            {showAdd && (
              <div className="flex gap-1 items-center">
                <select value={pickedLang} onChange={(e) => setPickedLang(e.target.value)} className="rounded border px-2 py-1 text-sm bg-transparent" style={borderStyle}>
                  <option value="">— pick —</option>
                  {languages.map((l) => (<option key={l.code} value={l.code}>{l.name} ({l.code})</option>))}
                </select>
                <button type="button" onClick={addLang} className="wofy-btn-ghost text-xs">Add</button>
              </div>
            )}
          </div>
          <p className="text-[10px] opacity-70 mt-2">
            AI translations use Emergent LLM · edit the result freely · click a language chip above to switch which one you're editing.
          </p>
        </div>
      )}
    </div>
  );
}
